import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import Breadcrumb from "@components/UI/Breadcrumb";
import { useFetchData } from "@hooks/useFetchData";
import { PrimaryButton } from "@components/Buttons";
import queryKeys from "@configs/react-query/query-key";
import { PolicyStep1Icon, PolicyStep2Icon, PolicyStep3Icon, PolicyStep4Icon, PolicyStep5Icon, TickIcon } from "@components/Icons";
import { ColorBox } from "@components/LicensePolicy";
import { LicensePolicyExceptionRuleTable } from "@components/Tables";
import { get_policy_attributes } from '@constants/licensePolicy';
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { Tooltip } from "react-tooltip";
import { useEffect, useState } from "react";
import { DEFAULT_LICENSE_POLICY } from "@constants/index";
import { useMutationData } from "@hooks/useMutationData";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import { ConfirmActionModal } from "@components/UI";

// Policy Step Item Component
const PolicyStepItem = ({ step, colors }) => {
  const { t } = useTranslation();
  const policyAttributes = get_policy_attributes(t);

  const policyMap = Object.fromEntries(
    get_policy_attributes(t).map(item => [item.code, item.name])
  );


  const translateApprovalStatus = (status) => {
    const statusMap = {
      "Approval": t("license-policy.approval"),
      "Disapproval": t("license-policy.disapproval"),
      "Conditional Approval": t("license-policy.conditional-approval"),
    };
    return statusMap[status] || status;
  };

  const getAttributeItemRemark = (item) => {
    return policyAttributes.find(attr => attr.code === (item.licensePolicyCd || item.code))?.remark;
  };

  const validLicensePolicyItems = step.licensePolicyStepItems?.filter(
    item => item.licensePolicyCd && item.licensePolicyCd !== "undefined"
  ) || [];

  const renderStepIcon = (stepNumber) => {
    switch (stepNumber) {
      case 1:
        return <PolicyStep1Icon size={48} />;
      case 2:
        return <PolicyStep2Icon size={48} />;
      case 3:
        return <PolicyStep3Icon size={48} />;
      case 4:
        return <PolicyStep4Icon size={48} />;
      case 5:
        return <PolicyStep5Icon size={48} />;
      default:
        return <PolicyStep1Icon size={48} />;
    }
  };

  return (
    <div className={`${colors.bgPolicyStepItem} rounded-lg border ${colors.borderPrimary} mb-4 relative min-h-46 max-h-72`}>
      {/* Left background accent with pointed shape */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-36 ${colors.bgPolicyStepItemLeftSide} rounded-l-lg`}
        style={{
          clipPath: 'polygon(0 0, calc(100% - 50px) 0, 100% 50%, calc(100% - 50px) 100%, 0 100%)'
        }}
      ></div>

      <div className="flex items-center relative z-10 h-full">
        {/* Step Number and Icon - Centered in the background area */}
        <div className="w-36 flex flex-col items-center justify-center py-6 h-full">
          <div className={`text-sm ${colors.textPrimary} font-medium mb-2`}>{t("license-policy.step")}</div>
          <div className="flex items-center justify-center">
            {renderStepIcon(step.policyStep)}
          </div>
        </div>

        {/* Step Details - Moved to the right */}
        <div className="flex-1 p-6 h-full flex flex-col justify-center">
          <div className="grid grid-cols-5 gap-8">
            {/* Left Column: Policy Step Color and License Use Approval */}
            <div className="space-y-4 col-span-1">
              {/* Policy Step Color */}
              <div>
                <div className={`text-sm ${colors.textSubTitle} font-medium mb-2`}>{t("license-policy.obligation-type-color")}</div>
                <ColorBox colorName={step.policyStepColor} size="md" t={t} />
              </div>

              {/* License Use Approval */}
              <div>
                <div className={`text-sm ${colors.textSubTitle} font-medium mb-2`}>{t("license-policy.license-use-approval")}</div>
                <div className={`text-sm ${colors.textPrimary}`}>{translateApprovalStatus(step.licenseUseApprovalCdName)}</div>
              </div>
            </div>

            {/* Right Column: Remark and Item List */}
            <div className="space-y-4 col-span-4">
              {/* Remark */}
              <div>
                <div className={`text-sm ${colors.textSubTitle} font-medium mb-2`}>{t("license-policy.remark")}</div>
                <p className={`text-sm ${colors.textPrimary} max-h-[90vh] overflow-y-auto scroll-smooth whitespace-pre-line`}>
                  {step.remark || "---"}
                </p>
              </div>

              {/* Item List */}
              {validLicensePolicyItems.length > 0 && (
                <div>
                  <div className={`text-sm ${colors.textSubTitle} font-medium mb-2`}>{t("license-policy.license-policy-item-list")}</div>
                  <div className="flex flex-wrap gap-2">
                    {validLicensePolicyItems.map((item, index) => (
                      <span
                        key={item.licensePolicyStepItemSeq}
                        className={`px-3 py-1 ${colors.policyItemChip} text-xs rounded-full cursor-default`}
                        data-tooltip-id={`tooltip-${item.licensePolicyStepItemSeq}`}
                        data-tooltip-content={`${policyMap[item.licensePolicyCd] || item.licensePolicyCdName}${getAttributeItemRemark(item) ? ` - ${getAttributeItemRemark(item)}` : ''}`}
                      >
                        {policyMap[item.licensePolicyCd] || item.licensePolicyCdName}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Tooltips for license policy items */}
      {validLicensePolicyItems.map((item) => (
        <Tooltip
          key={`tooltip-${item.licensePolicyStepItemSeq}`}
          id={`tooltip-${item.licensePolicyStepItemSeq}`}
          place="top"
          className={`${colors.bgTooltip} ${colors.textTooltip} !text-xs !rounded-lg !px-3 !py-2 !max-w-[300px] !min-w-[200px] !z-50`}
          style={{
            backgroundColor: colors.headerTooltipBackgroundColor,
            color: colors.headerTooltipTextColor,
            fontSize: '12px',
            borderRadius: '8px',
            padding: '8px 12px',
            maxWidth: '300px',
            minWidth: '200px',
            zIndex: 50
          }}
        >
          <div className="font-medium break-words">{item.licensePolicyCdName}</div>
          {getAttributeItemRemark(item) && (
            <div className={`${colors.textSecondary} mt-1 break-words leading-relaxed`}>
              {getAttributeItemRemark(item)}
            </div>
          )}
        </Tooltip>
      ))}
    </div>
  );
};

const LicensePolicyDetail = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { licensePolicySeq } = useParams();
  const { hasEditPermission } = useRoutePermissions();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Fetch specific policy details
  const { data: policyData, isLoading, isFetching, error } = useFetchData(
    [queryKeys.getLicensePolicyByLicenseId, licensePolicySeq],
    `${queryKeys.getLicensePolicyByLicenseId}/${licensePolicySeq}`,
    { method: "GET",
      params: {
        isDefault: true,
      },
     },
    {},
    {},
  );

  useEffect(() => {
    if (error) {
      if (error?.response?.status === 404) {
        navigate("/404", { state: { backTo: "/license-policy" } });
      }
    }
  }, [error]);

  useEffect(() => {
    if (policyData) {
      setPolicy(policyData?.object || policyData);
    }
  }, [policyData]);

  const handleEditPolicy = () => {
    navigate(`/license-policy/${licensePolicySeq}/edit`);
  };

  const resetDefaultLicensePolicy = useMutationData(
    "/license_policy/reset",
    { method: "PUT" },
    {
      onSuccess: (response) => {
        showSuccessToast(t("license-policy.reset-default-license-policy-success"));
      },
      onError: (error) => {
        showErrorToast(t("license-policy.reset-default-license-policy-error"));
      },
    },
  );

  const handleResetPolicy = () => {
    setShowResetConfirm(true);
  };

  const handleConfirmReset = async () => {
    setShowResetConfirm(false);
    const response = await resetDefaultLicensePolicy.mutateAsync();
    if (response) {
      setPolicy(response);
    }
  };

  const breadcrumbItems = [
    { label: t("license-policy.license-policy"), href: "/license-policy" },
    { label: t("license-policy.license-policy-details") },
  ];

  if (isFetching) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div className={`flex h-full flex-col ${colors.bgSecondary}`} data-testid="license-policy-detail-page">
      {/* Breadcrumb */}
      <div className="flex items-center text-sm">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between py-4">
        <h1 className={`text-lg font-semibold ${colors.textPrimary} uppercase`}>
          {policy?.policyName}
        </h1>
        {hasEditPermission && (
          <div className="flex gap-3">
            {policy?.policyName === DEFAULT_LICENSE_POLICY.POLICY_NAME && (
              <PrimaryButton onClick={handleResetPolicy} disabled={policy?.defaultLicensePolicyEdited !== true}>
                {t("common.reset")}
              </PrimaryButton>
            )}
            <PrimaryButton onClick={handleEditPolicy}>
              {t("license-policy.edit-license-policy")}
            </PrimaryButton>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-grow overflow-y-auto" data-testid="license-policy-detail-form">
        {isLoading ? (
          // Loading skeleton
          <div className="space-y-4">
            <div className={`${colors.bgPrimary} rounded-lg p-6 animate-pulse border ${colors.borderPrimary}`}>
              <div className={`h-6 ${colors.bgSkeletonDark} rounded mb-4 w-1/3`}></div>
              <div className="grid grid-cols-3 gap-8">
                <div>
                  <div className={`h-4 ${colors.bgSkeletonDark} rounded mb-2 w-20`}></div>
                  <div className={`h-4 ${colors.bgSkeletonDark} rounded w-16`}></div>
                </div>
                <div>
                  <div className={`h-4 ${colors.bgSkeletonDark} rounded mb-2 w-16`}></div>
                  <div className={`h-4 ${colors.bgSkeletonDark} rounded w-24`}></div>
                </div>
                <div>
                  <div className={`h-4 ${colors.bgSkeletonDark} rounded mb-2 w-20`}></div>
                  <div className={`h-4 ${colors.bgSkeletonDark} rounded w-12`}></div>
                </div>
              </div>
            </div>
          </div>
        ) : policy ? (
          <>
            {/* Policy Information */}
            <div className={`${colors.bgPrimary} rounded-lg p-6 border ${colors.borderPrimary} mb-6`}>
              <h2 className={`text-md font-medium ${colors.textPrimary} mb-4 uppercase`}>{t("license-policy.policy-information")}</h2>
              <div className="grid grid-cols-[2fr_3fr_1fr] gap-6">
                <div className="min-w-0">
                  <div className={`text-sm ${colors.textSubTitle} font-medium mb-2`}>{t("license-policy.policy-name")}</div>
                  <div className={`text-sm ${colors.textPrimary} break-words overflow-hidden`}>{policy.policyName}</div>
                </div>
                <div className="min-w-0">
                  <div className={`text-sm ${colors.textSubTitle} font-medium mb-2`}>{t("license-policy.remark")}</div>
                  <p className={`text-sm ${colors.textPrimary} whitespace-pre-line break-words`}>
                    {policy.remark || "---"}
                  </p>
                </div>
                <div>
                  <div className={`text-sm ${colors.textSubTitle} font-medium mb-2`}>{t("license-policy.default-policy")}</div>
                    <div className={`text-sm ${colors.textPrimary}`}>{policy.globalFlag === 'Y' ? t("common.yes") : t("common.no")}</div>
                </div>
              </div>
            </div>

            {/* License Policy Item List */}
            <div className={`${colors.bgPrimary} rounded-lg p-6 border ${colors.borderPrimary} mb-6`}>
              <h2 className={`text-md font-medium ${colors.textPrimary} mb-4 uppercase`}>{t("license-policy.license-policy-item-list")}</h2>
              {policy.licensePolicySteps && policy.licensePolicySteps.length > 0 && (
                policy.licensePolicySteps
                  .sort((a, b) => a.policyStep - b.policyStep)
                  .map((step) => (
                    <PolicyStepItem
                      key={step.licensePolicyStepSeq}
                      step={step}
                      colors={colors}
                    />
                  ))
              )}
            </div>

            {/* License Policy Exception Rule List */}
            <div className={`${colors.bgPrimary} rounded-lg p-6 border ${colors.borderPrimary} mb-6`}>
              <h2 className={`text-md font-medium ${colors.textPrimary} mb-4 uppercase`}>{t("license-policy.license-policy-rules-exception-list")}</h2>
              <LicensePolicyExceptionRuleTable
                data={policy.licensePolicyRules}
                className="w-full"
                onDataChange={() => { }}
                isEditMode={false}
              />
            </div>
          </>
        ) : (
          // No data state
          <div className={`${colors.bgPrimary} rounded-lg p-6 border ${colors.borderPrimary} text-center`}>
            <p className={`${colors.textSubTitle} mb-2`}>{t("license-policy.policy-not-found")}</p>
            <p className={`${colors.textSubTitle} text-sm`}>{t("license-policy.the-requested-policy-could-not-be-found")}</p>
          </div>
        )}
      </div>

      {/* Reset Confirmation Modal */}
      <ConfirmActionModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={handleConfirmReset}
        title={t("license-policy.confirm-reset-title")}
        description={t("license-policy.confirm-reset-description")}
        confirmText={t("common.reset")}
        loading={resetDefaultLicensePolicy.isPending}
      />
    </div>
  );
};

export default LicensePolicyDetail;
